import styles from "../css/verificationQueue.module.scss";
import { formatDate } from "common/service/CommonService";
import { Column } from "Components/Common/CommonTableIndexWise";
import { ModuleQueueConfig, ModuleUiConfig, DetailSection, BadgeConfig } from "./verification.types";

// ==========================================
// QUEUE CONFIG INTERFACES & CONSTANTS
// ==========================================


export const QUEUE_MODULE_CONFIGS: Record<string, ModuleQueueConfig> = {
  BLOCK: {
    label: "Block",
    badgeIcon: "ri-user-forbid-line",
    badgeClass: styles.blockBadge,
    queryKey: "block-volunteer-select-all",
    idParamName: "blockid",
    mainMessage: "Volunteer block request has been initiated.",
    getSubMessage: (detail) => `Please verify the blocking of volunteer (Initials: ${detail?.initials || "-"}) for reason: ${detail?.blockreasontext || "Exclusion"}.`,
  },
  DROPOUT: {
    label: "Dropout",
    badgeIcon: "ri-user-unfollow-line",
    badgeClass: styles.dropoutBadge,
    queryKey: "dropout-volunteer-select-all",
    idParamName: "volunteerdropoutid",
    mainMessage: "Volunteer dropout request has been initiated.",
    getSubMessage: (detail) => `Please verify the dropout of volunteer (Initials: ${detail?.initials || "-"}) for reason: ${detail?.dropouttypetext || "Withdrawal"}.`,
  },
  TRANSFER: {
    label: "Transfer",
    badgeIcon: "ri-swap-box-line",
    badgeClass: styles.transferBadge,
    queryKey: "transfer-volunteer-select-all",
    idParamName: "volunteertransferid",
    mainMessage: "Subject transfer request has been initiated.",
    getSubMessage: (detail) => `Please verify the transfer of volunteer (Initials: ${detail?.initials || "-"}) from Study ${detail?.fromstudycode || "-"} to ${detail?.tostudycode || "-"}.`,
  },
  IP_INVENTORY: {
    label: "IP Inventory",
    badgeIcon: "ri-archive-line",
    badgeClass: styles.ipInventoryBadge,
    queryKey: "ipinventory-selectAll",
    idParamName: "ipdetailid",
    mainMessage: "IP Inventory verification request has been initiated.",
    getSubMessage: (detail) => `Please verify the IP receipt of product ${detail?.productname || "-"} (Batch: ${detail?.batchlotno || "-"}) for Study ${detail?.studycode || "-"}.`,
  },
  RETENTION: {
    label: "Retention",
    badgeIcon: "ri-briefcase-line",
    badgeClass: styles.retentionBadge,
    queryKey: "retention-selectAll",
    idParamName: "retentionid",
    mainMessage: "IP Retention verification request has been initiated.",
    getSubMessage: (detail) => `Please verify the IP retention of product ${detail?.productname || "-"} (Retention No: ${detail?.retentionnumber || "-"}) for Study ${detail?.studycode || "-"}.`,
  },
  DISPENSING: {
    label: "Dispensing",
    badgeIcon: "ri-hand-coin-line",
    badgeClass: styles.dispensingBadge,
    queryKey: "dispensing-select-all",
    idParamName: "dispensingid",
    mainMessage: "IP Dispensing verification request has been initiated.",
    getSubMessage: (detail) => `Please verify the IP dispensing of product ${detail?.productname || "-"} (Log ID: ${detail?.logid || "-"}) for Study ${detail?.studycode || "-"}.`,
  },
  RETURNS_LOG: {
    label: "Returns Log",
    badgeIcon: "ri-refund-2-line",
    badgeClass: styles.returnsLogBadge,
    queryKey: "returns-accountability-select-all",
    idParamName: "returnid",
    mainMessage: "IP Returns Log verification request has been initiated.",
    getSubMessage: (detail) => `Please verify the IP return of product ${detail?.productname || "-"} (Return No: ${detail?.returnno || "-"}) for Study ${detail?.studycode || "-"}.`,
  },
  DRUG_TRANSFER: {
    label: "Drug Transfer",
    badgeIcon: "ri-swap-box-line",
    badgeClass: styles.drugTransferBadge,
    queryKey: "transfer-selectAll",
    idParamName: "transferid",
    mainMessage: "IP Drug Transfer verification request has been initiated.",
    getSubMessage: (detail) => `Please verify the IP transfer of product ${detail?.productname || "-"} (Transfer No: ${detail?.transfernumber || "-"}) for Study ${detail?.studycode || "-"}.`,
  },
  DISPOSAL: {
    label: "Disposal",
    badgeIcon: "ri-delete-bin-line",
    badgeClass: styles.disposalBadge,
    queryKey: "disposal-selectAll",
    idParamName: "disposalid",
    mainMessage: "IP Disposal verification request has been initiated.",
    getSubMessage: (detail) => `Please verify the IP disposal of product ${detail?.productname || "-"} (Disposal No: ${detail?.disposalnumber || "-"}) for Study ${detail?.studycode || "-"}.`,
  },
  RECONCILIATION: {
    label: "Reconciliation",
    badgeIcon: "ri-scales-3-line",
    badgeClass: styles.reconciliationBadge,
    queryKey: "daily-accountability-selectAll",
    idParamName: "accountabilityid",
    mainMessage: "Daily Reconciliation verification request has been initiated.",
    getSubMessage: (detail) => `Please verify the daily reconciliation for product ${detail?.productname || "-"} (Day: ${detail?.accountabilityday ? formatDate(detail.accountabilityday) : "-"}) for Study ${detail?.studycode || "-"}.`,
  },
  ADVERSE_EVENT: {
    label: "Adverse Event",
    badgeIcon: "ri-pulse-line",
    badgeClass: styles.adverseEventBadge,
    queryKey: "adverse-event-select-all",
    idParamName: "adverseeventid",
    mainMessage: "Adverse Event reporting verification request has been initiated.",
    getSubMessage: (detail) => `Please verify the Adverse Event (${detail?.adverseeventnumber || "-"}) for Subject: ${detail?.subjectno || "-"} (Term: ${detail?.eventterm || "-"}).`,
  },
  AMBULATORY_VISIT: {
    label: "Ambulatory Visit",
    badgeIcon: "ri-hospital-line",
    badgeClass: styles.ambulatoryVisitBadge,
    queryKey: "selectall-ambulatory-visit",
    idParamName: "visitid",
    mainMessage: "Ambulatory Visit verification request has been initiated.",
    getSubMessage: (detail) => `Please verify the Ambulatory Visit: ${detail?.visitname || "-"} (Number: ${detail?.visitnumber || "-"}) for Study ${detail?.studycode || "-"}.`,
  },
  LAB_TEST: {
    label: "Lab Test",
    badgeIcon: "ri-flask-line",
    badgeClass: styles.labTestBadge,
    queryKey: "lab-tests-selectAll",
    idParamName: "studylabtestid",
    mainMessage: "Lab Test verification request has been initiated.",
    getSubMessage: (detail) => `Please verify the Lab Test: ${detail?.reportname || "-"} (Initials: ${detail?.initials || "-"}) for Study ${detail?.studycode || "-"}.`,
  },
  SAMPLE_COLLECTION: {
    label: "Sample Collection",
    badgeIcon: "ri-test-tube-line",
    badgeClass: styles.sampleCollectionBadge,
    queryKey: "sample-collection-selectAll",
    idParamName: "sessionid",
    mainMessage: "Sample Collection session is pending action.",
    getSubMessage: (detail) => `Study: ${detail?.studycode || "-"} | Period : ${detail?.period_number || "-"}`,
  },
};

// ==========================================
// DETAIL MODAL CONFIG INTERFACES & CONSTANTS
// ==========================================

export const MODULE_UI_CONFIGS: Record<string, ModuleUiConfig> = {
  BLOCK: {
    label: "Block",
    badgeIcon: "ri-user-forbid-line",
    heroIcon: "ri-user-forbid-line",
    badgeClass: styles.blockBadge,
    heroClass: styles.heroBlock,
    heroTitlePrefix: "Volunteer",
  },
  DROPOUT: {
    label: "Dropout",
    badgeIcon: "ri-user-unfollow-line",
    heroIcon: "ri-user-unfollow-line",
    badgeClass: styles.dropoutBadge,
    heroClass: styles.heroDropout,
    heroTitlePrefix: "Volunteer",
  },
  TRANSFER: {
    label: "Transfer",
    badgeIcon: "ri-swap-box-line",
    heroIcon: "ri-swap-line",
    badgeClass: styles.transferBadge,
    heroClass: styles.heroTransfer,
    heroTitlePrefix: "Volunteer",
  },
  IP_INVENTORY: {
    label: "IP Inventory",
    badgeIcon: "ri-archive-line",
    heroIcon: "ri-archive-line",
    badgeClass: styles.ipInventoryBadge,
    heroClass: styles.heroIpInventory,
    heroTitlePrefix: "IP Receipt",
  },
  RETENTION: {
    label: "Retention",
    badgeIcon: "ri-briefcase-line",
    heroIcon: "ri-briefcase-line",
    badgeClass: styles.retentionBadge,
    heroClass: styles.heroRetention,
    heroTitlePrefix: "IP Retention",
  },
  DISPENSING: {
    label: "Dispensing",
    badgeIcon: "ri-hand-coin-line",
    heroIcon: "ri-hand-coin-line",
    badgeClass: styles.dispensingBadge,
    heroClass: styles.heroDispensing,
    heroTitlePrefix: "IP Dispensing",
  },
  RETURNS_LOG: {
    label: "Returns Log",
    badgeIcon: "ri-refund-2-line",
    heroIcon: "ri-refund-2-line",
    badgeClass: styles.returnsLogBadge,
    heroClass: styles.heroReturnsLog,
    heroTitlePrefix: "IP Return",
  },
  DRUG_TRANSFER: {
    label: "Drug Transfer",
    badgeIcon: "ri-swap-box-line",
    heroIcon: "ri-swap-box-line",
    badgeClass: styles.drugTransferBadge,
    heroClass: styles.heroDrugTransfer,
    heroTitlePrefix: "IP Drug Transfer",
  },
  DISPOSAL: {
    label: "Disposal",
    badgeIcon: "ri-delete-bin-line",
    heroIcon: "ri-delete-bin-line",
    badgeClass: styles.disposalBadge,
    heroClass: styles.heroDisposal,
    heroTitlePrefix: "IP Disposal",
  },
  RECONCILIATION: {
    label: "Reconciliation",
    badgeIcon: "ri-scales-3-line",
    heroIcon: "ri-scales-3-line",
    badgeClass: styles.reconciliationBadge,
    heroClass: styles.heroReconciliation,
    heroTitlePrefix: "Daily Reconciliation",
  },
  ADVERSE_EVENT: {
    label: "Adverse Event",
    badgeIcon: "ri-pulse-line",
    heroIcon: "ri-pulse-line",
    badgeClass: styles.adverseEventBadge,
    heroClass: styles.heroAdverseEvent,
    heroTitlePrefix: "Adverse Event",
  },
  AMBULATORY_VISIT: {
    label: "Ambulatory Visit",
    badgeIcon: "ri-hospital-line",
    heroIcon: "ri-hospital-line",
    badgeClass: styles.ambulatoryVisitBadge,
    heroClass: styles.heroAmbulatoryVisit,
    heroTitlePrefix: "Ambulatory Visit",
  },
  LAB_TEST: {
    label: "Lab Test",
    badgeIcon: "ri-flask-line",
    heroIcon: "ri-flask-line",
    badgeClass: styles.labTestBadge,
    heroClass: styles.heroLabTest,
    heroTitlePrefix: "Lab Test",
  },
};

// ==========================================
// SAMPLE COLLECTION COLUMNS CONFIG
// ==========================================

export const buildSampleCollectionColumns = (): Column[] => [
  {
    key: "timepoint_number",
    label: "Time Point",
  },
  {
    key: "dosetime",
    label: "Dose Time",
    render: (val) => val ?? "-",
  },
  {
    key: "scheduledate",
    label: "Scheduled Date",
    render: (val, row) => {
      const d = val ?? row?.samplecollectiondate;
      return d ? formatDate(d) : "-";
    },
  },
  {
    key: "actualscheduletime",
    label: "Actual Schedule Time",
    render: (val) => val ?? "-",
  },
  {
    key: "samplecollectiontime",
    label: "Sample Collection Time",
    render: (val) => val ?? "-",
  },
  {
    key: "dosingstationno",
    label: "Dosing Station No",
    render: (val) => val ?? "-",
  },
];

// ==========================================
// DYNAMIC LAYOUT SECTION BUILDER
// ==========================================

export const getRecordSections = (rec: any): DetailSection[] => {
  if (!rec) return [];
  const { recorddetail: detail, moduletype } = rec;
  if (moduletype === "LAB_TEST") {
    return [
      {
        title: "Study & Subject Info",
        icon: "ri-book-open-line",
        fields: [
          { label: "Study Code", value: detail.studycode || "-" },
          { label: "Subject Initials", value: detail.initials || "-" },
        ]
      },
      {
        title: "Lab Test Details",
        icon: "ri-flask-line",
        fields: [
          { label: "Report Name", value: detail.reportname || "-" },
          { label: "Filename", value: detail.filename || "-" },
          { label: "File Size", value: detail.filesize || "-" },
          { label: "Verify Status", value: detail.verifystatus || "-" },
        ]
      }
    ];
  }

  if (moduletype === "AMBULATORY_VISIT") {
    return [
      {
        title: "Study & Visit Info",
        icon: "ri-book-open-line",
        fields: [
          { label: "Study Code", value: detail.studycode || "-" },
          { label: "Visit Number", value: detail.visitnumber || "-" },
          { label: "Visit Name", value: detail.visitname || "-" },
          { label: "Description", value: detail.visitdescription || "-" },
        ]
      },
      {
        title: "Visit Details",
        icon: "ri-calendar-event-line",
        fields: [
          { label: "Visit Type", value: detail.visittypetext || "-" },
          { label: "Visit Category", value: detail.visitcategorytext || "-" },
          { label: "Scheduled Date", value: formatDate(detail.scheduleddate) || "-" },
          { label: "Visit Start Day", value: detail.visitstartday ?? "-" },
          { label: "Visit End Day", value: detail.visitendday ?? "-" },
          { label: "Visit Status", value: detail.visitstatus || "-" },
        ]
      }
    ];
  }

  if (moduletype === "ADVERSE_EVENT") {
    return [
      {
        title: "Study & Subject Info",
        icon: "ri-book-open-line",
        fields: [
          { label: "Study Code", value: detail.studycode || "-" },
          { label: "Subject No", value: detail.subjectno || "-" },
          { label: "Event ID", value: detail.adverseeventnumber || "-" },
        ]
      },
      {
        title: "Adverse Event Details",
        icon: "ri-pulse-line",
        fields: [
          { label: "Event Term (MedDRA)", value: detail.eventterm || "-" },
          { label: "Onset Date", value: formatDate(detail.onsetdate) || "-" },
          { label: "Event Status", value: detail.eventstatus || "-" },
          { label: "Resolution Date", value: detail.resolutiondate ? formatDate(detail.resolutiondate) : "—" },
          { label: "Severity Grade", value: detail.severitygrade || "-" },
          { label: "Causality", value: detail.causality || "-" },
          { label: "Action Taken", value: detail.actiontaken || "-" },
          ...(detail.otheraction ? [{ label: "Other Action", value: detail.otheraction }] : []),
          { label: "Serious (SAE)?", value: detail.isadverse ? "Yes" : "No" },
        ]
      }
    ];
  }

  if (moduletype === "IP_INVENTORY") {
    return [
      {
        title: "Study & Product Info",
        icon: "ri-book-open-line",
        fields: [
          { label: "Study Code", value: detail.studycode || "-" },
          { label: "Product Name", value: detail.productname || "-" },
          { label: "Category", value: detail.ipcategory || "-" },
          { label: "Strength", value: detail.strength || "-" },
          { label: "Batch/Lot No", value: detail.batchlotno || "-" },
        ]
      },
      {
        title: "Receipt Details",
        icon: "ri-archive-line",
        fields: [
          { label: "Receipt Number", value: detail.receiptnumber || "-" },
          { label: "Received Qty", value: detail.receivedqty || "-" },
          { label: "Delivered By", value: detail.deliveredby || "-" },
          { label: "Date of Receipt", value: formatDate(detail.dateofreceipt) || "-" },
        ]
      }
    ];
  }

  if (moduletype === "RETENTION") {
    return [
      {
        title: "Study & Product Info",
        icon: "ri-book-open-line",
        fields: [
          { label: "Study Code", value: detail.studycode || "-" },
          { label: "Product Name", value: detail.productname || "-" },
          { label: "Category", value: detail.ipcategory || "-" },
          { label: "Strength", value: detail.strength || "-" },
          { label: "Batch/Lot No", value: detail.batchlotno || "-" },
        ]
      },
      {
        title: "Retention Details",
        icon: "ri-briefcase-line",
        fields: [
          { label: "Retention Number", value: detail.retentionnumber || "-" },
          { label: "Retention Period", value: detail.retentionperiod || "-" },
          { label: "IP Quantity", value: detail.ipquantity || "-" },
          { label: "Retention Quantity", value: detail.retentionquantity || "-" },
          { label: "Start Date", value: formatDate(detail.retentionstartdate) || "-" },
          { label: "Expiry Date", value: formatDate(detail.retentionexpirydate) || "-" },
        ]
      }
    ];
  }

  if (moduletype === "DISPENSING") {
    return [
      {
        title: "Study & Product Info",
        icon: "ri-book-open-line",
        fields: [
          { label: "Study Code", value: detail.studycode || "-" },
          { label: "Product Name", value: detail.productname || "-" },
          { label: "Category", value: detail.ipcategory || "-" },
          { label: "Batch/Lot No", value: detail.batchlotno || "-" },
          { label: "Expiry Date", value: formatDate(detail.expirydate) || "-" },
        ]
      },
      {
        title: "Dispensing Details",
        icon: "ri-hand-coin-line",
        fields: [
          { label: "Log ID", value: detail.logid || "-" },
          { label: "Available Qty", value: detail.availablequantity || "-" },
          { label: "Subject Range", value: `${detail.startsubjectno || "-"} to ${detail.endsubjectno || "-"}` },
          { label: "Qty Per Subject", value: detail.quantitypersubject || "-" },
          { label: "Excluded Subjects", value: detail.excludedsubjects || "-" },
          { label: "Total Qty Needed", value: detail.totalquantityneeded || "-" },
        ]
      }
    ];
  }

  if (moduletype === "RETURNS_LOG") {
    return [
      {
        title: "Study & Product Info",
        icon: "ri-book-open-line",
        fields: [
          { label: "Study Code", value: detail.studycode || "-" },
          { label: "Product Name", value: detail.productname || "-" },
          { label: "Category", value: detail.ipcategory || "-" },
          { label: "Batch/Lot No", value: detail.batchlotno || "-" },
          { label: "Unit Type", value: detail.unittype || "-" },
        ]
      },
      {
        title: "Returns Details",
        icon: "ri-refund-2-line",
        fields: [
          { label: "Return No", value: detail.returnno || "-" },
          { label: "Source Type", value: detail.returnsourcetype || "-" },
          ...(detail.subjectno ? [{ label: "Subject No", value: detail.subjectno }] : []),
          { label: "Quantity Returned", value: detail.quantityreturned || "-" },
          { label: "Condition", value: detail.conditionreturned || "-" },
          { label: "Reason for Return", value: detail.reasonforreturn || "-" },
          { label: "Return Date", value: formatDate(detail.returndate) || "-" },
        ]
      }
    ];
  }

  if (moduletype === "DRUG_TRANSFER") {
    return [
      {
        title: "Study & Product Info",
        icon: "ri-book-open-line",
        fields: [
          { label: "Study Code", value: detail.studycode || "-" },
          { label: "Product Name", value: detail.productname || "-" },
          { label: "Category", value: detail.ipcategory || "-" },
          { label: "Strength", value: detail.strength || "-" },
          { label: "Batch/Lot No", value: detail.batchlotno || "-" },
        ]
      },
      {
        title: "Drug Transfer Details",
        icon: "ri-swap-box-line",
        fields: [
          { label: "Transfer No", value: detail.transfernumber || "-" },
          { label: "Transfer Type", value: detail.transfertype || "-" },
          { label: "Transfer From", value: detail.transferfrom || "-" },
          { label: "Transfer To", value: detail.transferto || "-" },
          { label: "Quantity Type", value: detail.quantitytype || "-" },
          { label: "Total Quantity", value: detail.totalquantity || "-" },
          { label: "Transfer Qty", value: detail.transferquantity || "-" },
        ]
      }
    ];
  }

  if (moduletype === "DISPOSAL") {
    return [
      {
        title: "Study & Product Info",
        icon: "ri-book-open-line",
        fields: [
          { label: "Study Code", value: detail.studycode || "-" },
          { label: "Product Name", value: detail.productname || "-" },
          { label: "Category", value: detail.ipcategory || "-" },
          { label: "Strength", value: detail.strength || "-" },
          { label: "Batch/Lot No", value: detail.batchlotno || "-" },
        ]
      },
      {
        title: "Disposal Details",
        icon: "ri-delete-bin-line",
        fields: [
          { label: "Disposal No", value: detail.disposalnumber || "-" },
          { label: "Disposal Reason", value: detail.disposalreason || "-" },
          { label: "Disposal Method", value: detail.disposalmethod || "-" },
          { label: "Quantity Type", value: detail.quantitytype || "-" },
          { label: "Total Quantity", value: detail.totalquantity || "-" },
          { label: "Disposal Qty", value: detail.disposalquantity || "-" },
          { label: "Location", value: detail.disposallocation || "-" },
          { label: "Vendor", value: detail.disposalvendor || "-" },
          { label: "Disposal Date", value: formatDate(detail.disposaldate) || "-" },
        ]
      }
    ];
  }

  if (moduletype === "RECONCILIATION") {
    return [
      {
        title: "Study & Product Info",
        icon: "ri-book-open-line",
        fields: [
          { label: "Study Code", value: detail.studycode || "-" },
          { label: "Product Name", value: detail.productname || "-" },
          { label: "Category", value: detail.ipcategory || "-" },
          { label: "Strength", value: detail.strength || "-" },
          { label: "Batch/Lot No", value: detail.batchlotno || "-" },
        ]
      },
      {
        title: "Reconciliation Metrics",
        icon: "ri-scales-3-line",
        fields: [
          { label: "Reconciliation Date", value: formatDate(detail.accountabilityday) || "-" },
          { label: "Opening Stock", value: detail.openingstock || 0 },
          { label: "Received", value: detail.received || 0 },
          { label: "Dispensed", value: detail.dispensed || 0 },
          { label: "Returned", value: detail.returned || 0 },
          { label: "Retention", value: detail.retention || 0 },
          { label: "Transferred", value: detail.transfered || 0 },
          { label: "Disposed", value: detail.disposed || 0 },
          { label: "Closing Stock", value: detail.closingstock || 0 },
          { label: "Actual Count", value: detail.actualcount || 0 },
          { label: "Daily Variance", value: detail.dailyvariance || 0 },
          { label: "Reason for Variance", value: detail.reason || "-" },
          { label: "Remarks", value: detail.remarks || "-" },
        ]
      }
    ];
  }

  const sections: DetailSection[] = [
    {
      title: "Volunteer Information",
      icon: "ri-user-line",
      fields: [
        { label: "Volunteer Initials", value: detail.initials || "-" },
        ...(detail.subjectno ? [{ label: "Subject No", value: detail.subjectno }] : []),
        ...(detail.studycode ? [{ label: "Study Code", value: detail.studycode || "-"}] : [])
      ]
    }
  ];

  if (moduletype === "BLOCK") {
    sections.push({
      title: "Block Details",
      icon: "ri-lock-line",
      fields: [
        { label: "Block Type", value: detail.blocktype },
        { label: "Duration", value: detail.blockduration },
        { label: "Start Date", value: formatDate(detail.startdate) },
        ...(detail.enddate ? [{ label: "End Date", value: formatDate(detail.enddate) }] : []),
        { label: "Block Reason", value: detail.blockreasontext },
        { label: "Block Source", value: detail.blocksource?.replace("-", " ") },
        ...(detail.rescreeningeligibility ? [{ label: "Rescreen Eligibility", value: detail.rescreeningeligibility }] : [])
      ]
    });
  } else if (moduletype === "DROPOUT") {
    sections.push({
      title: "Dropout Details",
      icon: "ri-logout-circle-line",
      fields: [
        { label: "Dropout Type", value: detail.dropouttypetext },
        { label: "Dropout Date", value: formatDate(detail.dropoutdate) },
        { label: "Effective Date", value: formatDate(detail.effectivedate) },
        { label: "Study Stage", value: detail.studystage },
        ...(detail.aereferenceno ? [{ label: "AE Reference No", value: detail.aereferenceno }] : []),
        { label: "Dropout Reason", value: detail.dropoutreasontext }
      ]
    });
  } else if (moduletype === "TRANSFER") {
    sections.push({
      title: "Transfer Details",
      icon: "ri-swap-line",
      fields: [
        { label: "From Study", value: detail.fromstudycode || "-" },
        { label: "To Study", value: detail.tostudycode || "-" },
        { label: "Transfer Type", value: detail.transfertypetext || "-" },
        { label: "Transfer Reason", value: detail.transferreasontext || "-" },
        { label: "Re-Screening Required", value: detail.isscreeningrequired ? "Yes" : "No" }
      ]
    });
  }

  return sections;
};

// ==========================================
// BADGE CONFIG HELPER
// ==========================================

export const getBadgeConfig = (type: string, isQueue: boolean = false): BadgeConfig => {
  const config = isQueue ? QUEUE_MODULE_CONFIGS[type] : MODULE_UI_CONFIGS[type];
  if (config) {
    return { label: config.label, icon: config.badgeIcon, className: config.badgeClass };
  }
  return { label: type, icon: "ri-notification-3-line", className: "" };
};
